import type {
  ConfigApi,
  DiscoveryApi,
  OAuthApi,
} from '@backstage/core-plugin-api';

/** True unless GitHub itself says the token is no good. */
export const githubAccepts = async (token: string): Promise<boolean> => {
  const res = await fetch('https://api.github.com/user', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.status !== 401;
};

export const EXPIRED_MESSAGE =
  'GitHub no longer accepts your sign-in token. Sign out of Backstage (Settings, Sign out), sign in again, then submit again - your answers are kept under Task List, Start Over';

/**
 * A GitHub user token that GitHub accepted just now.
 *
 * Backstage's own session only refreshes when it thinks the token is about to
 * expire, by its own clock. GitHub can reject the token well before that - on
 * 2026-10-21 a token that passed the form-open check was refused with a 401
 * when the task ran minutes later. Asking the session again would hand back
 * the same dead token, so on a 401 this goes to the auth backend's refresh
 * endpoint directly, which trades the refresh cookie for a new token from
 * GitHub.
 */
export const freshGithubToken = async (deps: {
  githubAuth: OAuthApi;
  discoveryApi: DiscoveryApi;
  configApi: ConfigApi;
}): Promise<string> => {
  const { githubAuth, discoveryApi, configApi } = deps;
  const cached = await githubAuth.getAccessToken([], { optional: false });
  if (await githubAccepts(cached)) {
    return cached;
  }

  const env = configApi.getOptionalString('auth.environment') ?? 'development';
  const res = await fetch(
    `${await discoveryApi.getBaseUrl('auth')}/github/refresh?optional&env=${env}`,
    {
      credentials: 'include',
      headers: { 'x-requested-with': 'XMLHttpRequest' },
    },
  );
  const refreshed: string | undefined = res.ok
    ? (await res.json())?.providerInfo?.accessToken
    : undefined;
  if (refreshed && (await githubAccepts(refreshed))) {
    return refreshed;
  }
  throw new Error(EXPIRED_MESSAGE);
};
